import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { fixImageUrl } from "../../utils/fixImageUrl";
import CarouselTop from "./CarouselTop";
import logoImg from "../Images/logo40.jpg";
import { supabase } from "../../lib/supabase";
import { API_BASE } from "../../api.js";
import HomeSidebar from "./HomeSidebar";
import "./Home.css";
import "../Events/Events.css";

const DEFAULT_STATS = [
  { value: "200+",  label: "Registered Artists",    label_ar: "فنان مسجل" },
  { value: "30+",   label: "Years of Heritage",      label_ar: "عاماً من التراث" },
  { value: "15+",   label: "Countries Represented",  label_ar: "دولة ممثلة" },
  { value: "1000+", label: "Artworks Catalogued",    label_ar: "عمل فني موثق" },
];

function Lightbox({ src, onClose }) {
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(0,0,0,0.92)",
      display: "flex", alignItems: "center", justifyContent: "center",
      cursor: "zoom-out",
    }}>
      <img src={src} alt="" style={{ maxWidth: "92vw", maxHeight: "92vh", borderRadius: 8, objectFit: "contain" }} />
      <button onClick={onClose} style={{
        position: "absolute", top: 20, right: 24,
        background: "none", border: "none", color: "#fff", fontSize: 28, cursor: "pointer",
      }}>✕</button>
    </div>
  );
}

export default function Home() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar" || i18n.language?.startsWith("ar");
  const [cms,            setCms]          = useState({});
  const [invitedArtists, setInvitedArtists] = useState([]);
  const [lightbox,       setLightbox]       = useState(null);
  const [liveNews,       setLiveNews]       = useState([]);
  const [newsLoading,    setNewsLoading]    = useState(true);
  const [featuredArtist, setFeaturedArtist] = useState(null);
  const [elderArtists,   setElderArtists]   = useState([]);
  const openLb = useCallback((src) => setLightbox(src), []);

  useEffect(() => {
    fetch(`${API_BASE}/getRally/invited`)
      .then(r => r.json())
      .then(d => setInvitedArtists(d.artists || []))
      .catch(() => {});

    axios
      .get("https://newsdata.io/api/1/news?category=arts,entertainment&q=sudanese+artist+OR+sudan+art+OR+sudanese+art&language=en&apikey=pub_265077d47c856ccb5511a0dc1d09a0fcdd41c")
      .then(res => setLiveNews(res.data?.results || []))
      .catch(() => {})
      .finally(() => setNewsLoading(false));
  }, []);

  useEffect(() => {
    const load = async () => {
      let { data } = await supabase
        .from("artists")
        .select("id, name, name_ar, specialty, specialty_ar, photo_url, intro, intro_ar")
        .eq("status", "approved")
        .eq("is_visible", true)
        .eq("is_elder", true)
        .order("sort_order", { nullsFirst: false })
        .order("name");

      let pool = data || [];
      if (!pool.length) {
        const { data: all } = await supabase
          .from("artists")
          .select("id, name, name_ar, specialty, specialty_ar, photo_url, intro, intro_ar")
          .eq("status", "approved")
          .eq("is_visible", true)
          .order("name")
          .limit(20);
        pool = all || [];
      }
      if (!pool.length) return;
      setElderArtists(pool);

      const { data: pin } = await supabase
        .from("page_sections")
        .select("body_en")
        .eq("page", "home")
        .eq("section_key", "featured_artist")
        .maybeSingle();

      const pinnedId = pin?.body_en?.trim();
      if (pinnedId) {
        const pinned = pool.find(a => a.id === pinnedId);
        if (pinned) { setFeaturedArtist(pinned); return; }
      }

      // Auto-rotate every 3 days, loop through all then restart
      const idx = Math.floor(Date.now() / (3 * 24 * 60 * 60 * 1000)) % pool.length;
      setFeaturedArtist(pool[idx]);
    };
    load().catch(() => {});
  }, []);

  useEffect(() => {
    supabase
      .from("page_sections")
      .select("*")
      .eq("page", "home")
      .eq("is_visible", true)
      .order("sort_order", { ascending: true })
      .then(({ data }) => {
        if (data) {
          const map = {};
          data.forEach((s) => { map[s.section_key] = s; });
          setCms(map);
        }
      });
  }, []);

  const cTitle = (key, defEn, defAr) => {
    const s = cms[key];
    if (!s) return isAr ? (defAr || defEn) : defEn;
    return isAr ? (s.title_ar || s.title_en || defAr || defEn) : (s.title_en || s.title_ar || defEn);
  };

  const cBody = (key, defEn, defAr) => {
    const s = cms[key];
    if (!s) return isAr ? (defAr || defEn) : defEn;
    return isAr ? (s.body_ar || s.body_en || defAr || defEn) : (s.body_en || s.body_ar || defEn);
  };

  const cImg = (key, defSrc) => cms[key]?.image_url || defSrc;

  const paras = (text) => text?.split("\n\n").filter(Boolean).map((p, i) => (
    <p key={i} className="home-body-text">{p}</p>
  ));

  // Stats: parse body_en as "value|label" per line, else use defaults
  const statItems = (() => {
    const s = cms["stats"];
    if (s) {
      const body = isAr ? (s.body_ar || s.body_en) : (s.body_en || s.body_ar);
      if (body) return body.split("\n").filter(Boolean).map((line) => {
        const [value, label] = line.split("|");
        return { value: value?.trim(), label: label?.trim() };
      });
    }
    return DEFAULT_STATS.map((s) => ({ value: s.value, label: isAr ? s.label_ar : s.label }));
  })();

  // Extra CMS-only sections not handled above
  const HANDLED = new Set(["stats", "intro", "revolution", "hassan_musa", "radio", "cta"]);
  const extraSections = Object.values(cms)
    .filter((s) => !HANDLED.has(s.section_key))
    .sort((a, b) => a.sort_order - b.sort_order);

  return (
    <div className="home-page" dir={isAr ? "rtl" : "ltr"}>

      {lightbox && <Lightbox src={lightbox} onClose={() => setLightbox(null)} />}

      {/* ── Stats Strip ─────────────────────────────────────────────────────── */}
      <section className="home-stats-strip">
        <div className="home-stats-inner">
          {statItems.map((s, i) => (
            <div key={i} className="home-stat">
              <span className="home-stat-value">{s.value}</span>
              <span className="home-stat-label">{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── Invited / Pending members (full-width, outside grid) ─────────────── */}
      {invitedArtists.length > 0 && (
        <section className="home-section" style={{ paddingBottom: 0 }}>
          <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 20px" }}>
            <p className="home-eyebrow" style={{ textAlign: "center" }}>
              {isAr ? "أعضاء جدد" : "New Members"}
            </p>
            <h2 className="home-section-heading" style={{ textAlign: "center", marginBottom: 8 }}>
              {isAr ? "أعضاء في الانتظار" : "Pending Members"}
            </h2>
            <p style={{ textAlign: "center", color: "var(--text-muted, #888)", fontSize: "0.9rem", marginBottom: 28, lineHeight: 1.6 }}>
              {isAr
                ? "هؤلاء الفنانون تمت دعوتهم للانضمام. ستظهر ملفاتهم بعد إكمال بياناتهم."
                : "These artists have been invited to join SPAR. Their full profiles will appear once they complete their details."}
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: 14 }}>
              {invitedArtists.map(a => (
                <div key={a._id} style={{
                  background: "var(--card-bg, #111)", border: "1px solid var(--border, #2a2a2a)",
                  borderRadius: 12, padding: "18px 10px", textAlign: "center",
                }}>
                  <div style={{ width: 52, height: 52, borderRadius: "50%", background: "var(--brand-soft, #1a1a2e)", margin: "0 auto 10px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.3rem" }}>
                    ✦
                  </div>
                  <div style={{ fontWeight: 600, fontSize: "0.85rem", marginBottom: 6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</div>
                  <span style={{ fontSize: "0.68rem", background: "var(--brand-soft, #1a1a2e)", color: "var(--muted, #888)", borderRadius: 8, padding: "2px 8px", display: "inline-block" }}>
                    {isAr ? "قيد الإعداد" : "Setting up"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Two-column layout: sidebar starts beside Art & Blood intro ────────── */}
      <div className="home-layout">
      <div className="home-main">

      {/* ── Art & Blood Intro ────────────────────────────────────────────────── */}
      <section className="home-section home-intro">
        <div className="home-intro-inner">
          <div className="home-intro-logo-col">
            <img
              src={cImg("intro", logoImg)}
              alt="SPAR Logo"
              className="home-logo"
              onClick={cms.intro?.image_clickable ? () => openLb(cImg("intro", logoImg)) : undefined}
              style={{ cursor: cms.intro?.image_clickable ? "zoom-in" : "default" }}
            />
            <div className="home-logo-text">
              <span className="home-logo-text-ar">تجمع التشكيليين السودانيين</span>
              <span className="home-logo-text-en">Sudanese Plastic Artists Rally</span>
            </div>
          </div>
          <div className="home-intro-text-col">
            <p className="home-eyebrow">{isAr ? "الفن والثقافة · السودان" : "Art & Culture · Sudan"}</p>
            <h2 className="home-section-heading">
              {cTitle("intro", "«Art and Blood»", "«الفن والدم»")}
            </h2>
            {paras(cBody("intro",
              "Art is a very big part of Sudan's rich culture. The country is home to many artists and some of the world's most notable figures — Ibrahim El-Salahi, Hassan Musa, Hussein Jamaan, Hussein Shariffe, and more. Although there aren't many national art galleries or cultural centres, members of Sudan's vibrant art scene have taken the initiative to establish galleries to promote and nurture the country's artists.\n\nUnder the regime's war against art and artists, before the December Revolution, creativity became resistance. Today, even amid renewed conflict, Sudanese artists continue to speak through their work — painting hope on rubble.",
              "الفن جزء كبير من الثقافة السودانية الغنية. تضم البلاد فنانين عديدين من أبرز الشخصيات في العالم. حتى في خضم الصراع المتجدد، يواصل الفنانون السودانيون التعبير من خلال أعمالهم."
            ))}
            <div className="home-intro-actions">
              <a href="/ArtistsList" className="spar-btn spar-btn-primary">
                {isAr ? "استعرض الفنانين" : "Explore Artists"}
              </a>
              <a href="/Events" className="spar-btn spar-btn-outline">
                {isAr ? "آخر الأخبار" : "Latest News"}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Sudan in the News Today (live only — distinct from Events page) ──── */}
      <section className="home-section" style={{ paddingTop: 0 }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 20px" }}>

          <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                <p className="ev-eyebrow" style={{ margin: 0 }}>
                  {isAr ? "السودان في الأخبار اليوم" : "Sudan in the News Today"}
                </p>
                <span className="ev-live-dot" aria-label="Live"><span />{isAr ? "مباشر" : "Live"}</span>
              </div>
              <h2 className="home-section-heading" style={{ marginBottom: 4 }}>
                {isAr ? "أحدث الأخبار" : "Today's Headlines"}
              </h2>
              <p style={{ fontSize: "0.82rem", color: "var(--text-3, #888)", margin: 0 }}>
                {isAr
                  ? "أخبار السودان المباشرة — تتجدد يومياً. للقصص الفنية المنتقاة اذهب إلى صفحة الأخبار."
                  : "Fresh Sudan news updated daily. For curated art stories visit the News page."}
              </p>
            </div>
            <a href="/Events" className="spar-btn spar-btn-outline" style={{ flexShrink: 0 }}>
              {isAr ? "القصص الفنية ←" : "Art Stories →"}
            </a>
          </div>

          {newsLoading && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, color: "var(--text-3)", fontSize: "0.85rem", padding: "24px 0" }}>
              <div className="ev-spinner" />
              {isAr ? "جارٍ تحميل الأخبار…" : "Loading today's news…"}
            </div>
          )}

          {!newsLoading && liveNews.length > 0 && (
            <div className="ev-news-grid">
              {liveNews.slice(0, 6).map((article, i) => (
                <a key={article.article_id || i} className="ev-news-card"
                  href={article.link || "#!"} target="_blank" rel="noopener noreferrer">
                  <div className="ev-news-img-wrap">
                    {article.image_url
                      ? <img src={article.image_url} alt={article.title} className="ev-news-img"
                          onError={e => { e.target.style.display = "none"; }} />
                      : <div className="ev-news-img ev-news-img-placeholder">
                          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.35">
                            <rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
                          </svg>
                        </div>
                    }
                  </div>
                  <div className="ev-news-body">
                    <div className="ev-news-meta">
                      <span className="ev-news-source">{article.source_id}</span>
                      <span className="ev-news-date">{article.pubDate?.slice(0, 10) || ""}</span>
                    </div>
                    <h4 className="ev-news-title">{article.title}</h4>
                    {article.description && (
                      <p className="ev-news-desc">
                        {article.description.length > 100 ? article.description.slice(0, 100) + "…" : article.description}
                      </p>
                    )}
                  </div>
                </a>
              ))}
            </div>
          )}

          {!newsLoading && liveNews.length === 0 && (
            <p style={{ fontSize: "0.85rem", color: "var(--text-3)", padding: "16px 0" }}>
              {isAr ? "لا توجد أخبار متاحة حالياً." : "No news available right now."}
            </p>
          )}
        </div>
      </section>

      {/* ── Artwork Slideshow ────────────────────────────────────────────────── */}
      <section className="home-section home-slideshow-section">
        <div className="home-slideshow-header">
          <h2 className="spar-section-title">
            {cTitle("slideshow", "Artwork Gallery", "معرض الأعمال الفنية")}
          </h2>
          <a href="/gallery" className="home-view-all">
            {isAr ? "عرض الكل →" : "View All →"}
          </a>
        </div>
        <div className="home-carousel-wrap">
          <CarouselTop />
        </div>
      </section>

      {/* ── Sudan Revolution Article ──────────────────────────────────────────── */}
      <section className="home-section">
        <div className="home-article-grid">
          <div className="home-article-text">
            <p className="home-eyebrow">
              {isAr ? "21 أبريل 2019 · الفنون والثقافة" : "21 April 2019 · Arts & Culture"}
            </p>
            <h2 className="home-section-heading">
              {cTitle("revolution", "Sudanese Artist Revolution", "ثورة الفنان السوداني")}
            </h2>
            <h3 className="home-article-sub">
              {isAr ? "قصة الفن والدم" : "The Story of Art and Blood"}
            </h3>
            <div className="home-article-body">
              {paras(cBody("revolution",
                "**«December Revolution»**\nThe Sudanese Revolution refers to a series of protests that took place from December 2018 to April 2019. Led by young people, women, students, and professionals under the slogan \"Tasgut Bas\" (Just Fall, That's All), the movement demanded the resignation of President al-Bashir.\n\n**«Gaining Momentum»**\nArtists became an integral part of the months-long sit-in at the military headquarters in Khartoum. Street murals, portraits, and song became the language of the revolution — a catalyst for change that the regime could not silence. After months of demonstrations, the military removed al-Bashir from power in April 2019.\n\nThe Sudanese Revolution stands as a symbol of people's aspirations for freedom, justice, and democratic governance. The legacy of the revolution will shape Sudan's future as it strives to build a more inclusive and democratic society.",
                "**«ثورة ديسمبر»**\nتشير الثورة السودانية إلى سلسلة من الاحتجاجات التي جرت من ديسمبر 2018 إلى أبريل 2019.\n\n**«اكتساب الزخم»**\nأصبح الفنانون جزءاً لا يتجزأ من الاعتصام في مقر القيادة العسكرية في الخرطوم."
              ))}
            </div>
          </div>
          <div className="home-article-media">
            <div className="home-video-wrap">
              <iframe
                src="https://www.youtube.com/embed/-pvl8vCd1VI"
                title="How protesters toppled a tyrant in Sudan"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
            <p className="home-video-caption">
              {isAr ? "كيف أسقط المحتجون طاغية في السودان · رويترز" : "How protesters toppled a tyrant in Sudan · Reuters Hotspots"}
            </p>
          </div>
        </div>
      </section>

      {/* ── Featured Artist (auto-rotates every 3 days through gold members) ── */}
      {featuredArtist && (() => {
        const name  = isAr ? featuredArtist.name_ar  || featuredArtist.name  : featuredArtist.name;
        const spec  = isAr ? featuredArtist.specialty_ar || featuredArtist.specialty : featuredArtist.specialty;
        const bio   = isAr ? featuredArtist.intro_ar  || featuredArtist.intro : featuredArtist.intro;
        const photo = fixImageUrl(featuredArtist.photo_url);
        const others = elderArtists.filter(a => a.id !== featuredArtist.id);
        return (
          <section className="home-section home-feature-quote">
            <div className="home-feature-inner">
              <div className="home-feature-img-col">
                {photo
                  ? <img src={photo} alt={name} className="home-feature-img" onError={e => { e.target.style.display = "none"; }} />
                  : <div className="home-feature-img" style={{ background: "var(--card-bg,#111)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 64, color: "#333" }}>👤</div>
                }
              </div>
              <div className="home-feature-text-col">
                <p className="home-eyebrow">{isAr ? "فنان مميز" : "Featured Artist"}</p>
                <h2 className="home-feature-name">{name}</h2>
                {spec && <p className="home-feature-role">{spec}</p>}
                {bio && (
                  <blockquote className="home-feature-quote-text">
                    {bio.length > 300 ? bio.slice(0, 300) + "…" : bio}
                  </blockquote>
                )}
                <a href={`/ArtistProfile/${featuredArtist.id}`} className="spar-btn spar-btn-outline">
                  {isAr ? "عرض الملف الكامل" : "View Full Profile"}
                </a>
              </div>
            </div>

            {/* Avatar strip — other gold artists */}
            {others.length > 0 && (
              <div className="home-featured-others">
                <p className="home-featured-others-label">
                  {isAr ? "فنانون مميزون آخرون" : "Other Featured Artists"}
                </p>
                <div className="home-featured-avatars">
                  {others.map(a => {
                    const n = isAr ? a.name_ar || a.name : a.name;
                    const p = fixImageUrl(a.photo_url);
                    return (
                      <button key={a.id} className="home-featured-avatar-btn" onClick={() => setFeaturedArtist(a)} title={n}>
                        {p
                          ? <img src={p} alt={n} className="home-featured-avatar-img" onError={e => { e.target.style.display = "none"; }} />
                          : <div className="home-featured-avatar-initial">{n?.[0]}</div>
                        }
                        <span className="home-featured-avatar-name">{n}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </section>
        );
      })()}

      {/* ── Dabanga Radio ────────────────────────────────────────────────────── */}
      <section className="home-section home-radio-section">
        <div className="home-radio-header">
          <div>
            <p className="home-eyebrow">{isAr ? "البث المباشر" : "Live Broadcast"}</p>
            <h2 className="spar-section-title" style={{ marginBottom: 6 }}>
              {cTitle("radio", "Dabanga Radio", "راديو دبنقا")}
            </h2>
            <p className="home-radio-desc">
              {cBody("radio",
                "Independent voice from Sudan — on-the-ground news, culture, and art coverage.",
                "الصوت المستقل من السودان — أخبار ميدانية وثقافة وتغطية فنية."
              )}
            </p>
          </div>
          <a
            href={cms.radio?.link_url || "https://soundcloud.com/radio-dabanga/sets/todaytalk"}
            target="_blank" rel="noopener noreferrer"
            className="spar-btn spar-btn-green"
          >
            {isAr
              ? (cms.radio?.link_label_ar || cms.radio?.link_label_en || "استمع على ساوندكلاود")
              : (cms.radio?.link_label_en || "Listen on SoundCloud")}
          </a>
        </div>
        <div className="home-radio-wrap">
          <iframe
            src="https://www.dabangasudan.org/radio-player/index.html"
            title="Dabanga Radio Player"
            frameBorder="0"
            className="home-radio-player"
          />
        </div>
      </section>

      {/* ── Extra CMS sections ───────────────────────────────────────────────── */}
      {extraSections.map((s) => {
        const title    = isAr ? (s.title_ar || s.title_en) : (s.title_en || s.title_ar);
        const body     = isAr ? (s.body_ar  || s.body_en)  : (s.body_en  || s.body_ar);
        const btnLabel = isAr ? (s.link_label_ar || s.link_label_en) : (s.link_label_en || s.link_label_ar);
        return (
          <section key={s.id} className="home-section">
            <div className="home-article-grid" style={{ display: "block", maxWidth: 860, margin: "0 auto" }}>
              {title && <h2 className="home-section-heading">{title}</h2>}
              {s.image_url && (
                <img src={s.image_url} alt={title || ""}
                  onClick={s.image_clickable ? () => openLb(s.image_url) : undefined}
                  onError={(e) => { e.target.style.display = "none"; }}
                  style={{
                    width: "100%", borderRadius: 12, marginBottom: 20, objectFit: "cover",
                    cursor: s.image_clickable ? "zoom-in" : "default",
                  }}
                />
              )}
              {paras(body)}
              {s.link_url && (
                <a href={s.link_url} target={s.link_target || "_self"}
                   rel={s.link_target === "_blank" ? "noopener noreferrer" : undefined}
                   className="spar-btn spar-btn-primary" style={{ marginTop: 12 }}>
                  {btnLabel || (isAr ? "اقرأ المزيد" : "Learn More")}
                </a>
              )}
            </div>
          </section>
        );
      })}

      </div>{/* /home-main */}
      <HomeSidebar news={liveNews} isAr={isAr} />
      </div>{/* /home-layout */}

      {/* ── Join CTA ─────────────────────────────────────────────────────────── */}
      <section className="home-cta-section">
        <div className="home-cta-inner">
          <div className="home-cta-flag-bar">
            <span className="flag-r" />
            <span className="flag-k" />
            <span className="flag-g" />
          </div>
          <h2 className="home-cta-title">
            {cTitle("cta", "Join the Sudanese Art Community", "انضم إلى مجتمع الفن السوداني")}
          </h2>
          <p className="home-cta-subtitle">
            {cBody("cta",
              "Register as an artist. Share your work. Connect with the global Sudanese creative diaspora.",
              "سجّل كفنان. شارك أعمالك. تواصل مع الشتات الإبداعي السوداني العالمي."
            )}
          </p>
          <div className="home-cta-actions">
            <a href={cms.cta?.link_url || "/signup"} className="spar-btn spar-btn-primary">
              {isAr
                ? (cms.cta?.link_label_ar || "أنشئ ملف الفنان")
                : (cms.cta?.link_label_en || "Create Artist Profile")}
            </a>
            <a href="/ArtistsList" className="spar-btn spar-btn-ghost">
              {isAr ? "تصفح جميع الفنانين" : "Browse All Artists"}
            </a>
          </div>
        </div>
      </section>


    </div>
  );
}
